import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Campaign } from '@/types/leads';
import { useTenantContext } from '@/hooks/useTenantContext';
import { useMetros } from '@/hooks/useReferenceData';
import { useBrands, type Brand } from '@/hooks/useBrands';
import { BrandsCard } from '@/components/settings/BrandsCard';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Plus, Edit, Trash2, Link as LinkIcon, PlusCircle, Wifi, WifiOff, AlertCircle, Clock } from 'lucide-react';
import { format, formatDistanceToNow, differenceInDays } from 'date-fns';
import { AddDailySpendDialog } from '@/components/campaigns/AddDailySpendDialog';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface LeadSource {
  id: string;
  name: string;
  is_active: boolean;
}

interface Metro {
  id: string;
  name: string;
}

interface CampaignWithCosts extends Campaign {
  period_budget?: number;
  period_actual_cost?: number;
  last_spend_sync_at?: string | null;
  data_source?: 'automatic' | 'manual' | 'mixed' | 'none';
  metros?: Metro;
  brands?: { id: string; name: string } | null;
}

export default function MarketingSources() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenantContext();
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const now = new Date();
    return format(new Date(now.getFullYear(), now.getMonth(), 1), 'yyyy-MM-01');
  });
  const [expandedSources, setExpandedSources] = useState<Set<string>>(new Set());
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<CampaignWithCosts | null>(null);
  const [showSpendDialog, setShowSpendDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignWithCosts | null>(null);
  
  // Fetch metros
  const { data: metros = [] } = useMetros(true);

  // Fetch trading names (brands) for the campaign dialog
  const { data: brands = [] } = useBrands(true);

  // Fetch lead sources
  const { data: sources } = useQuery({
    queryKey: ['lead-sources'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lead_sources')
        .select('*')
        .order('name');
      if (error) throw error;
      return data as LeadSource[];
    },
  });

  // Fetch campaigns with period costs and data source info
  const { data: campaigns } = useQuery({
    queryKey: ['campaigns-with-costs', selectedPeriod],
    queryFn: async () => {
      const { data: campaignsData, error: campaignsError } = await supabase
        .from('campaigns')
        .select('*, last_spend_sync_at, metro_id, metros(id, name), brands(id, name)')
        .order('name');
      
      if (campaignsError) throw campaignsError;

      const { data: periodCosts, error: costsError } = await supabase
        .from('campaign_period_costs')
        .select('campaign_id, budget, actual_cost')
        .eq('period', selectedPeriod);

      if (costsError) throw costsError;

      // Get data source info (check if campaign has webhook data)
      const startOfMonth = new Date(selectedPeriod);
      const endOfMonth = new Date(startOfMonth.getFullYear(), startOfMonth.getMonth() + 1, 0);
      
      const { data: dailySpend } = await supabase
        .from('campaign_daily_spend')
        .select('campaign_id, source_platform')
        .gte('spend_date', format(startOfMonth, 'yyyy-MM-dd'))
        .lte('spend_date', format(endOfMonth, 'yyyy-MM-dd'));

      const costMap = new Map(periodCosts?.map(pc => [pc.campaign_id, pc]) || []);
      
      // Determine data source type for each campaign
      const dataSourceMap = new Map<string, 'automatic' | 'manual' | 'mixed'>();
      dailySpend?.forEach(ds => {
        const currentType = dataSourceMap.get(ds.campaign_id);
        const isManual = ds.source_platform === 'manual';
        const isAutomatic = ds.source_platform !== 'manual';
        
        if (!currentType) {
          dataSourceMap.set(ds.campaign_id, isManual ? 'manual' : 'automatic');
        } else if (
          (currentType === 'manual' && isAutomatic) ||
          (currentType === 'automatic' && isManual)
        ) {
          dataSourceMap.set(ds.campaign_id, 'mixed');
        }
      });

      return campaignsData.map((c: any) => ({
        ...c,
        period_budget: costMap.get(c.id)?.budget || 0,
        period_actual_cost: costMap.get(c.id)?.actual_cost || 0,
        data_source: dataSourceMap.get(c.id) || 'none',
        last_spend_sync_at: c.last_spend_sync_at,
        metros: c.metros,
        brands: c.brands,
      })) as CampaignWithCosts[];
    },
  });

  // Helper to get freshness badge
  const getFreshnessBadge = (lastSync: string | null | undefined, hasExternalId: boolean) => {
    if (!lastSync) {
      if (hasExternalId) {
        return { variant: 'destructive' as const, label: 'Never synced', icon: AlertCircle, color: 'text-destructive' };
      }
      return { variant: 'secondary' as const, label: 'Manual only', icon: Clock, color: 'text-muted-foreground' };
    }
    
    const days = differenceInDays(new Date(), new Date(lastSync));
    
    if (days <= 2) {
      return { variant: 'default' as const, label: formatDistanceToNow(new Date(lastSync), { addSuffix: true }), icon: Wifi, color: 'text-success' };
    }
    if (days <= 7) {
      return { variant: 'outline' as const, label: `${days}d ago`, icon: Clock, color: 'text-warning' };
    }
    return { variant: 'destructive' as const, label: `${days}d stale`, icon: WifiOff, color: 'text-destructive' };
  };

  // Save campaign mutation
  const saveCampaign = useMutation({
    mutationFn: async (values: any) => {
      const { period_budget, ...campaignData } = values;
      
      if (editingCampaign) {
        // Update existing campaign
        const { error } = await supabase
          .from('campaigns')
          .update(campaignData)
          .eq('id', editingCampaign.id);
        if (error) throw error;

        // Update period cost if budget provided
        if (period_budget !== undefined) {
          const { error: costError } = await supabase
            .from('campaign_period_costs')
            .upsert({
              campaign_id: editingCampaign.id,
              period: selectedPeriod,
              budget: period_budget,
              company_id: editingCampaign.company_id,
            }, {
              onConflict: 'campaign_id,period'
            });
          if (costError) throw costError;
        }
      } else {
        // Create new campaign
        const { data: newCampaign, error } = await supabase
          .from('campaigns')
          .insert({
            ...campaignData,
            company_id: tenantId,
          })
          .select()
          .single();

        if (error) throw error;

        // Create initial period cost if budget provided
        if (period_budget !== undefined && period_budget > 0) {
          const { error: costError } = await supabase
            .from('campaign_period_costs')
            .insert({
              campaign_id: newCampaign.id,
              period: selectedPeriod,
              budget: period_budget,
              company_id: newCampaign.company_id,
            });
          if (costError) throw costError;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns-with-costs'] });
      toast.success(editingCampaign ? 'Campaign updated' : 'Campaign created');
      setShowCampaignDialog(false);
      setEditingCampaign(null);
    },
    onError: (error) => {
      toast.error(`Failed to save campaign: ${error.message}`);
    },
  });

  // Delete campaign mutation
  const deleteCampaign = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('campaigns').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns-with-costs'] });
      toast.success('Campaign deleted');
    },
    onError: (error) => {
      toast.error(`Failed to delete campaign: ${error.message}`);
    },
  });

  // Delete lead source mutation — moves any campaigns to Unassigned, then deletes the source
  const deleteLeadSource = useMutation({
    mutationFn: async (sourceId: string) => {
      const { error: updateError } = await supabase
        .from('campaigns')
        .update({ lead_source_id: null })
        .eq('lead_source_id', sourceId);
      if (updateError) throw updateError;

      const { error } = await supabase.from('lead_sources').delete().eq('id', sourceId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-sources'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns-with-costs'] });
      toast.success('Lead source deleted');
    },
    onError: (error) => {
      toast.error(`Failed to delete lead source: ${error.message}`);
    },
  });

  const toggleSource = (sourceId: string) => {
    setExpandedSources(prev => {
      const next = new Set(prev);
      if (next.has(sourceId)) {
        next.delete(sourceId);
      } else {
        next.add(sourceId);
      }
      return next;
    });
  };

  const getCampaignsBySource = (sourceId: string | null) => {
    return campaigns?.filter(c => c.lead_source_id === sourceId) || [];
  };

  const getSourceBudget = (sourceId: string) => {
    const sourceCampaigns = getCampaignsBySource(sourceId);
    return sourceCampaigns.reduce((sum, c) => sum + (c.period_budget || 0), 0);
  };

  const unassignedCampaigns = getCampaignsBySource(null);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Marketing Sources & Campaigns</h2>
          <p className="text-muted-foreground">Manage your marketing channels and campaign budgets</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label>Period:</Label>
            <Input
              type="month"
              value={selectedPeriod.substring(0, 7)}
              onChange={(e) => setSelectedPeriod(e.target.value + '-01')}
              className="w-40"
            />
          </div>
          <Button onClick={() => {
            setEditingCampaign(null);
            setShowCampaignDialog(true);
          }}>
            <Plus className="mr-2 h-4 w-4" />
            Add Campaign
          </Button>
        </div>
      </div>

      <BrandsCard />

      <Card className="p-6">
        <div className="space-y-2">
          {sources?.map(source => {
            const isExpanded = expandedSources.has(source.id);
            const sourceCampaigns = getCampaignsBySource(source.id);
            const budget = getSourceBudget(source.id);

            return (
              <div key={source.id} className="border rounded-lg">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleSource(source.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleSource(source.id);
                    }
                  }}
                  className="w-full flex items-center justify-between p-4 hover:bg-accent transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <span className="font-semibold">{source.name}</span>
                    {!source.is_active && <span className="text-xs text-muted-foreground">(Inactive)</span>}
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-muted-foreground">
                      Budget: ${budget.toFixed(2)}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      ({sourceCampaigns.length} {sourceCampaigns.length === 1 ? 'campaign' : 'campaigns'})
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        const count = sourceCampaigns.length;
                        const message = count > 0
                          ? `Delete "${source.name}"? Its ${count} ${count === 1 ? 'campaign' : 'campaigns'} will be moved to Unassigned.`
                          : `Delete "${source.name}"?`;
                        if (confirm(message)) {
                          deleteLeadSource.mutate(source.id);
                        }
                      }}
                      title="Delete lead source"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-4 pb-4 space-y-2">
                    {sourceCampaigns.map(campaign => {
                      const freshness = getFreshnessBadge(
                        campaign.last_spend_sync_at,
                        !!campaign.google_ads_campaign_id || !!campaign.external_campaign_id,
                      );
                      const googleStatus = campaign.google_ads_campaign_id ? campaign.google_ads_status : null;
                      const FreshnessIcon = freshness.icon;
                      
                      return (
                      <div key={campaign.id} className="flex items-center justify-between p-3 bg-muted/50 rounded">
                        <div className="flex items-center gap-3">
                          <span>{campaign.name}</span>
                          {campaign.brands?.name && (
                            <Badge variant="outline" className="text-xs font-normal">{campaign.brands.name}</Badge>
                          )}
                          {campaign.external_campaign_id && (
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <LinkIcon className="h-3 w-3" />
                              {campaign.external_campaign_id}
                            </span>
                          )}
                          {googleStatus === 'PAUSED' && (
                            <Badge variant="secondary" className="text-xs font-normal">Paused in Google</Badge>
                          )}
                          {googleStatus === 'REMOVED' && (
                            <Badge variant="secondary" className="text-xs font-normal">Removed from Google</Badge>
                          )}
                          {!campaign.is_active && (
                            <span className="text-xs text-muted-foreground">(Inactive)</span>
                          )}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant={freshness.variant} className="text-xs gap-1">
                                  <FreshnessIcon className={`h-3 w-3 ${freshness.color}`} />
                                  {freshness.label}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Last spend data: {campaign.last_spend_sync_at 
                                  ? format(new Date(campaign.last_spend_sync_at), 'PPpp')
                                  : 'Never received'}</p>
                                {campaign.google_ads_campaign_id
                                  ? <p className="text-xs text-muted-foreground">Spend synced nightly from Google Ads</p>
                                  : campaign.external_campaign_id
                                  ? <p className="text-xs text-muted-foreground">Connected to Google Ads automation</p>
                                  : <p className="text-xs text-muted-foreground">Manual entry only - no automation</p>}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm">${campaign.period_budget?.toFixed(2) || '0.00'}</span>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedCampaign(campaign);
                                setShowSpendDialog(true);
                              }}
                              title="Add Daily Spend"
                            >
                              <PlusCircle className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingCampaign(campaign);
                                setShowCampaignDialog(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                if (confirm('Delete this campaign?')) {
                                  deleteCampaign.mutate(campaign.id);
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    )})}
                  </div>
                )}
              </div>
            );
          })}

          {unassignedCampaigns.length > 0 && (
            <div className="border rounded-lg border-dashed">
              <div className="p-4">
                <h3 className="font-semibold mb-2">📋 Unassigned Campaigns</h3>
                <div className="space-y-2">
                  {unassignedCampaigns.map(campaign => (
                    <div key={campaign.id} className="flex items-center justify-between p-3 bg-muted/30 rounded">
                      <span>{campaign.name}</span>
                          {campaign.brands?.name && (
                            <Badge variant="outline" className="text-xs font-normal">{campaign.brands.name}</Badge>
                          )}
                      <div className="flex items-center gap-2">
                        <span className="text-sm">${campaign.period_budget?.toFixed(2) || '0.00'}</span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingCampaign(campaign);
                            setShowCampaignDialog(true);
                          }}
                        >
                          Assign
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>

      <CampaignDialog
        open={showCampaignDialog}
        onOpenChange={setShowCampaignDialog}
        campaign={editingCampaign}
        sources={sources || []}
        metros={metros}
        brands={brands}
        selectedPeriod={selectedPeriod}
        onSave={(values) => saveCampaign.mutate(values)}
      />

      <AddDailySpendDialog
        open={showSpendDialog}
        onOpenChange={setShowSpendDialog}
        campaignId={selectedCampaign?.id || ''}
        campaignName={selectedCampaign?.name || ''}
        companyId={tenantId || ''}
      />
    </div>
  );
}

interface CampaignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: CampaignWithCosts | null;
  sources: LeadSource[];
  metros: Metro[];
  brands: Brand[];
  selectedPeriod: string;
  onSave: (values: Partial<Campaign> & { period_budget?: number }) => void;
}

function CampaignDialog({ open, onOpenChange, campaign, sources, metros, brands, selectedPeriod, onSave }: CampaignDialogProps) {
  const [formData, setFormData] = useState({
    name: campaign?.name || '',
    lead_source_id: campaign?.lead_source_id || '',
    brand_id: campaign?.brand_id || '',
    metro_id: campaign?.metro_id || '',
    external_campaign_id: campaign?.external_campaign_id ?? '',
    tracking_number: campaign?.tracking_number ?? '',
    description: campaign?.description ?? '',
    period_budget: campaign?.period_budget || 0,
    is_active: campaign?.is_active ?? true,
  });

  useEffect(() => {
    if (open) {
      setFormData({
        name: campaign?.name || '',
        lead_source_id: campaign?.lead_source_id || '',
        brand_id: campaign?.brand_id || '',
        metro_id: campaign?.metro_id || '',
        external_campaign_id: campaign?.external_campaign_id ?? '',
        tracking_number: campaign?.tracking_number ?? '',
        description: campaign?.description ?? '',
        period_budget: campaign?.period_budget || 0,
        is_active: campaign?.is_active ?? true,
      });
    }
  }, [campaign, open]);

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast.error('Campaign name is required');
      return;
    }

    onSave({
      ...formData,
      lead_source_id: formData.lead_source_id || null,
      brand_id: formData.brand_id || null,
      metro_id: formData.metro_id || null,
      external_campaign_id: formData.external_campaign_id || null,
      tracking_number: formData.tracking_number || null,
      description: formData.description || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{campaign ? 'Edit Campaign' : 'Create Campaign'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Campaign Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Sydney Plumbing Campaign"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="lead_source">Lead Source</Label>
              <Select
                value={formData.lead_source_id || 'none'}
                onValueChange={(value) => setFormData({ ...formData, lead_source_id: value === 'none' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select source (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (Unassigned)</SelectItem>
                  {sources.map(source => (
                    <SelectItem key={source.id} value={source.id}>
                      {source.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="metro">Default Metro</Label>
              <Select
                value={formData.metro_id || 'none'}
                onValueChange={(value) => setFormData({ ...formData, metro_id: value === 'none' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select metro (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {metros.map(metro => (
                    <SelectItem key={metro.id} value={metro.id}>
                      {metro.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Fallback metro for leads without a matching postcode
              </p>
            </div>
          </div>

          <div>
            <Label htmlFor="brand">Trading name</Label>
            <Select
              value={formData.brand_id || 'none'}
              onValueChange={(value) => setFormData({ ...formData, brand_id: value === 'none' ? '' : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select trading name (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Company default</SelectItem>
                {brands.map(brand => (
                  <SelectItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              What technicians see as "Trading as" on jobs from this campaign. Leaving it on the company default hides nothing else.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="external_id">External Campaign ID</Label>
              <Input
                id="external_id"
                value={formData.external_campaign_id || ''}
                onChange={(e) => setFormData({ ...formData, external_campaign_id: e.target.value })}
                placeholder="gads-12345"
              />
            </div>

            <div>
              <Label htmlFor="tracking">Tracking Number</Label>
              <Input
                id="tracking"
                value={formData.tracking_number || ''}
                onChange={(e) => setFormData({ ...formData, tracking_number: e.target.value })}
                placeholder="+61 400 000 000"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="budget">Budget for {format(new Date(selectedPeriod), 'MMMM yyyy')}</Label>
            <Input
              id="budget"
              type="number"
              min="0"
              step="0.01"
              value={formData.period_budget}
              onChange={(e) => setFormData({ ...formData, period_budget: parseFloat(e.target.value) || 0 })}
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Campaign details..."
            />
          </div>

          <div className="flex items-center gap-2">
            <Switch
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
            <Label>Active</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>
            {campaign ? 'Update Campaign' : 'Create Campaign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
